/**
 * DataSyncPage - "Build Data Model"
 *
 * Entry point: ConnectorsPage → "Build Data Model" button → /data-sync
 *
 * Shows all active connectors.  For each connector the user can verify /
 * configure which tables / resources are selected.  When every connector
 * has at least one item selected the user can start a sync job that runs:
 *   Refresh Schema → Profile Tables → Build Data Model
 */
import React from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Paper,
  TextField,
  Divider,
  IconButton,
  Checkbox,
  Tooltip,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AppDialog } from '../components/dialog';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import SyncRounded from '@mui/icons-material/SyncRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import ArrowForwardIosRounded from '@mui/icons-material/ArrowForwardIosRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import TableChartRounded from '@mui/icons-material/TableChartRounded';
import AccountTreeRounded from '@mui/icons-material/AccountTreeRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import InsertDriveFileRounded from '@mui/icons-material/InsertDriveFileRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import { ReactComponent as GoogleDriveSvg } from '../assets/connectors/google-drive.svg';
import { ReactComponent as PostgresSvg } from '../assets/connectors/postgres.svg';
import { ReactComponent as MySQLSvg } from '../assets/connectors/mysql.svg';
import { ReactComponent as OneDriveSvg } from '../assets/connectors/onedrive.svg';
import { ReactComponent as SharePointSvg } from '../assets/connectors/sharepoint.svg';
import { ReactComponent as GoogleAdsSvg } from '../assets/connectors/google-ads.svg';
import { ReactComponent as BigQuerySvg } from '../assets/connectors/bigquery.svg';
import { ReactComponent as SnowflakeSvg } from '../assets/connectors/Snowflake.svg';
import { ReactComponent as RedshiftSvg } from '../assets/connectors/redshift.svg';
import { ReactComponent as ShopifySvg } from '../assets/connectors/shopify.svg';
import { ReactComponent as MCPSvg } from '../assets/connectors/mcp.svg';
import { buildApiUrl } from '../config';
import { SnackbarContext } from '../ui/SnackbarProvider';
import DriveFilesPickerModal, { DriveFile } from '../components/DriveFilesPickerModal';
import { formatRelativeTime } from '../utils/date';
import { useProductOnboarding } from '../hooks/useProductOnboarding';
import OnboardingCoachmark from '../components/OnboardingCoachmark';

// ---------------------------------------------------------------------------
// Connector icon map
// ---------------------------------------------------------------------------

const CONNECTOR_ICON_MAP: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  postgres:     PostgresSvg,
  mysql:        MySQLSvg,
  google_drive: GoogleDriveSvg,
  snowflake:    SnowflakeSvg,
  redshift:     RedshiftSvg,
  onedrive:     OneDriveSvg,
  sharepoint:   SharePointSvg,
  google_ads:   GoogleAdsSvg,
  bigquery:     BigQuerySvg,
  shopify:      ShopifySvg,
  mcp:          MCPSvg,
};

export function ConnectorIcon({ type, size = 24 }: { type: string; size?: number }) {
  const IconSvg = CONNECTOR_ICON_MAP[type];
  return (
    <Box
      sx={(t) => ({
        width: size + 10, height: size + 10, borderRadius: `${t.ds.radius.sm}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: t.tokens.surface.raised,
        flexShrink: 0,
      })}
    >
      {IconSvg
        ? <IconSvg width={size} height={size} />
        : <StorageRounded sx={{ fontSize: size, color: 'text.disabled' }} />}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface ConnectorFull {
  connector_id: string;
  name: string | null;
  type: string;
  category: string; // 'sql' | 'files' | 'rest'
  status: string;
  last_schema_fetch: string | null;
  connector_metadata?: {
    selected_table_names?: string[];
    selected_drive_files?: {
      id: string;
      name: string;
      mimeType?: string;
      /** Optional. When omitted, the file's full sheet/tab list is synced. */
      selected_sheets?: string[] | null;
    }[];
  };
}

export type StepStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface SubSubStep {
  id: string; label: string; status: StepStatus; error?: string | null;
}
export interface SubStep {
  id: string; label: string; status: StepStatus; error?: string | null; substeps?: SubSubStep[];
}
export interface Step {
  id: string; label: string; status: StepStatus; error?: string | null; substeps?: SubStep[];
}
export interface SyncJob {
  job_id: string;
  tenant_id: string;
  connector_ids: string[];
  status: string;
  progress: { steps: Step[] } | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

// ---------------------------------------------------------------------------
// Shared helpers (re-exported for DataSyncJobPage)
// ---------------------------------------------------------------------------

export function StatusChip({ status }: { status: string }) {
  const map: Record<string, { label: string; color: 'default' | 'primary' | 'success' | 'error' }> = {
    pending: { label: 'Pending', color: 'default' },
    running: { label: 'Running', color: 'primary' },
    completed: { label: 'Completed', color: 'success' },
    failed: { label: 'Failed', color: 'error' },
  };
  const cfg = map[status] || { label: status, color: 'default' };
  return <Chip label={cfg.label} color={cfg.color} size="small" variant={status === 'running' ? 'filled' : 'outlined'} />;
}

export function parseUtcDate(input: string): Date {
  if (input.includes('T') && !input.endsWith('Z') && !input.includes('+') && !input.split('T')[1]?.includes('-')) {
    return new Date(input + 'Z');
  }
  return new Date(input);
}

export function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

/** Returns count of selected items for a connector, or 0 */
function selectionCount(conn: ConnectorFull): number {
  if (conn.category === 'files') {
    return conn.connector_metadata?.selected_drive_files?.length ?? 0;
  }
  return conn.connector_metadata?.selected_table_names?.length ?? 0;
}

function selectionLabel(conn: ConnectorFull): string {
  if (conn.category === 'files') return 'files';
  if (conn.category === 'rest') return 'resources';
  return 'tables';
}

// ---------------------------------------------------------------------------
// Inline table / resource picker modal
// ---------------------------------------------------------------------------

interface Resource { id: string; name: string; mimeType?: string; type?: string; }

function TablePickerModal({
  open,
  connector,
  tenantId,
  authHeader,
  onClose,
  onSaved,
}: {
  open: boolean;
  connector: ConnectorFull | null;
  tenantId: string;
  authHeader: () => Record<string, string>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const theme = useTheme();
  const { notify } = React.useContext(SnackbarContext);
  const [resources, setResources] = React.useState<Resource[]>([]);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    if (!open || !connector) return;
    setError(null);
    setSearch('');
    setLoading(true);
    fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connector.connector_id}/resources`), {
      headers: authHeader() as HeadersInit,
    })
      .then(r => { if (!r.ok) throw new Error('Could not fetch resources'); return r.json(); })
      .then((data: Resource[]) => {
        setResources(data || []);
        // Pre-select already saved items
        const saved = connector.category === 'files'
          ? (connector.connector_metadata?.selected_drive_files || []).map((f: any) => f.id)
          : (connector.connector_metadata?.selected_table_names || []);
        setSelectedIds(saved);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, connector?.connector_id]);

  const label = connector ? selectionLabel(connector) : 'items';
  const isDrive = connector?.category === 'files';

  const filtered = resources.filter(r => {
    if (!search) return true;
    return (r.name || r.id).toLowerCase().includes(search.toLowerCase());
  });

  // Group by schema prefix only for SQL tables ("public.users") - never for Drive files
  const groups: Record<string, Resource[]> = {};
  filtered.forEach(r => {
    const name = r.name || r.id;
    const dot = name.indexOf('.');
    const schema = (!isDrive && dot > 0) ? name.substring(0, dot) : '__all__';
    if (!groups[schema]) groups[schema] = [];
    groups[schema].push(r);
  });
  const schemaKeys = Object.keys(groups).sort();
  const hasSchemas = schemaKeys.length > 1 || (schemaKeys.length === 1 && schemaKeys[0] !== '__all__');

  const toggle = (id: string) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSave = async () => {
    if (!connector) return;
    setSaving(true);
    try {
      const patchBody = isDrive
        ? { connector_metadata: { selected_drive_files: resources.filter(r => selectedIds.includes(r.id)).map(r => ({ id: r.id, name: r.name, mimeType: r.mimeType })) } }
        : { connector_metadata: { selected_table_names: selectedIds } };
      const r = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connector.connector_id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(patchBody),
      });
      if (!r.ok) throw new Error('Failed to save selection');
      notify(`${selectedIds.length} ${label} saved`, 'success');
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const titleLabel = isDrive ? 'Select files & folders' : `Select ${label}`;
  const subtitleParts = [
    connector?.name || connector?.type,
    `${resources.length} available`,
    `${selectedIds.length} selected`,
  ].filter(Boolean);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="md"
      icon={connector ? <ConnectorIcon type={connector.type} size={20} /> : undefined}
      title={titleLabel}
      subtitle={subtitleParts.join(' · ')}
      busy={saving}
      contentSx={{ px: 0, py: 0 }}
      primaryAction={{
        label: saving ? 'Saving…' : `Save${selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}`,
        onClick: handleSave,
        disabled: loading || selectedIds.length === 0,
      }}
      secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: onClose }}
      actionsLeftExtra={
        <Typography variant="caption" color="text.secondary">
          {selectedIds.length > 0 ? `${selectedIds.length} ${label} selected` : `No ${label} selected`}
        </Typography>
      }
    >
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1.5, pb: 0 }}>
        {!loading && !error && resources.length > 0 && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5, alignItems: { xs: 'stretch', sm: 'center' } }}>
            <TextField
              placeholder={`Search ${label}…`}
              size="small"
              fullWidth
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              sx={{
                flex: { sm: '1 1 0%' },
                minWidth: 0,
                '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.8125rem', height: 36 },
              }}
            />
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-end', sm: 'flex-start' }} sx={{ flexShrink: 0 }}>
              <Button size="small" variant="outlined"
                onClick={() => {
                  const selectable = isDrive
                    ? filtered.filter(r => r.mimeType !== 'application/vnd.google-apps.folder')
                    : filtered;
                  setSelectedIds(selectable.map(r => r.id));
                }}
                sx={{ borderRadius: '7px', textTransform: 'none', fontSize: '0.75rem', whiteSpace: 'nowrap', height: 36, px: 1.5 }}>
                Select All
              </Button>
              {selectedIds.length > 0 && (
                <Button size="small" variant="text"
                  onClick={() => setSelectedIds([])}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', whiteSpace: 'nowrap', height: 36, px: 1, color: 'text.secondary' }}>
                  Clear
                </Button>
              )}
            </Stack>
          </Stack>
        )}
        <Divider />
      </Box>

      <Box sx={{ px: 3, py: 0, flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        ) : filtered.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1 }}>
            <StorageRounded sx={{ fontSize: 40, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              {search ? 'No results found' : `No ${label} available`}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ py: 1 }}>
            {schemaKeys.map(schemaKey => (
              <Box key={schemaKey}>
                {hasSchemas && (
                  <Typography variant="caption" fontWeight={700} color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem', px: 1, py: 0.75, display: 'block' }}>
                    {schemaKey}
                  </Typography>
                )}
                {groups[schemaKey].map((r: Resource) => {
                  const name = r.name || r.id;
                  const displayName = hasSchemas && name.includes('.') ? name.substring(name.indexOf('.') + 1) : name;
                  const isSelected = selectedIds.includes(r.id);
                  const isFolder = isDrive && r.mimeType === 'application/vnd.google-apps.folder';
                  const ItemIcon = isDrive
                    ? (isFolder ? FolderRounded : InsertDriveFileRounded)
                    : TableChartRounded;
                  const iconColor = isDrive
                    ? (isFolder ? 'warning.main' : (isSelected ? 'primary.main' : 'text.disabled'))
                    : (isSelected ? 'primary.main' : 'text.disabled');
                  return (
                    <Box
                      key={r.id}
                      onClick={() => toggle(r.id)}
                      sx={(t) => ({
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        px: 1, py: 0.75, borderRadius: `${t.ds.radius.sm}px`, cursor: 'pointer',
                        bgcolor: isSelected ? t.tokens.brand.primarySoft : 'transparent',
                        '&:hover': { bgcolor: isSelected ? t.tokens.brand.primarySoft : t.tokens.surface.sunken },
                      })}
                    >
                      <Checkbox checked={isSelected} size="small" disableRipple sx={{ p: 0 }}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        onChange={() => toggle(r.id)} />
                      <ItemIcon sx={{ fontSize: 16, color: iconColor, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: isSelected ? 500 : 400 }} noWrap>
                        {displayName}
                      </Typography>
                      {isDrive ? (
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem', flexShrink: 0 }}>
                          {isFolder ? 'Folder' : 'File'}
                        </Typography>
                      ) : r.type ? (
                        <Chip label={r.type} size="small" variant="outlined"
                          sx={(t) => ({ fontSize: '0.6875rem', height: 20, borderRadius: `${t.ds.radius.sm}px`, flexShrink: 0 })} />
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </AppDialog>
  );
}

// ---------------------------------------------------------------------------
// Pipeline steps explainer
// ---------------------------------------------------------------------------

function PipelineExplainer({ connectorCount }: { connectorCount: number }) {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const steps = [
    { icon: <RefreshRounded sx={{ fontSize: 20 }} />, label: 'Refresh Schema', desc: 'Fetch the latest schema of the selected tables from each connector', color: theme.palette.primary.main, ai: false },
    { icon: <TableChartRounded sx={{ fontSize: 20 }} />, label: 'Profile Tables', desc: 'Compute statistics and data quality metrics', color: theme.palette.warning.main, ai: false },
    { icon: <AccountTreeRounded sx={{ fontSize: 20 }} />, label: 'Build Data Model', desc: 'AI detects what changed and applies updates incrementally, preserving your edits', color: theme.palette.success.main, ai: true },
  ];
  return (
    <Paper variant="outlined" sx={(t) => ({ borderRadius: `${t.ds.radius.md}px`, overflow: 'hidden', mb: 2.5, bgcolor: t.tokens.surface.sunken, borderColor: t.tokens.border.subtle })}>
      {/* Section header with divider */}
      <Box sx={(t) => ({
        px: { xs: 1.75, sm: 2 }, py: 1.25,
        borderBottom: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.sunken,
        display: 'flex', alignItems: 'center', gap: 1,
      })}>
        <SyncRounded sx={{ fontSize: 14, color: 'text.disabled' }} />
        <Typography variant="caption" fontWeight={600} color="text.secondary"
          sx={{ letterSpacing: 0.3, fontSize: '0.75rem' }}>
          What happens when you run
        </Typography>
      </Box>
      <Box sx={{ p: { xs: 1.75, sm: 2 } }}>
        <Stack direction={isMdDown ? 'column' : 'row'} spacing={0}>
          {steps.map((s, i) => (
            <React.Fragment key={s.label}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 0.75, minWidth: 0 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${s.color}18`, color: s.color, border: `1.5px solid ${s.color}40` }}>
                  {s.icon}
                </Box>
                <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="center">
                  <Typography variant="caption" fontWeight={600} sx={{ lineHeight: 1.3 }}>{s.label}</Typography>
                  {s.ai && <AutoAwesomeRounded sx={{ fontSize: 11, color: 'primary.main' }} />}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, fontSize: '0.75rem', px: { xs: 0.5, sm: 0 } }}>{s.desc}</Typography>
              </Box>
              {i < steps.length - 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    py: isMdDown ? 0.5 : 0,
                    pt: isMdDown ? 0 : 2.25,
                  }}
                >
                  <ArrowForwardIosRounded
                    sx={{
                      fontSize: 12,
                      color: 'text.disabled',
                      mx: isMdDown ? 0 : 1,
                      transform: isMdDown ? 'rotate(90deg)' : 'none',
                    }}
                  />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Stack>
      </Box>
      {/* Scope note - sits as a quiet footer strip inside the card */}
      <Box sx={(t) => ({
        px: { xs: 1.75, sm: 2 }, py: 1,
        borderTop: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.sunken,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 1,
      })}>
        <StorageRounded sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
          Runs across all <strong>{connectorCount} active connector{connectorCount !== 1 ? 's' : ''}</strong>.
          {' '}To exclude a connector,{' '}
          <Button size="small" variant="text" onClick={() => navigate('/connectors')} sx={{ p: 0, minWidth: 'unset', fontSize: '0.75rem', fontWeight: 600, verticalAlign: 'baseline' }}>
            deactivate it first →
          </Button>
        </Typography>
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function DataSyncPage() {
  const { shouldShow, currentStepId } = useProductOnboarding();
  const navigate = useNavigate();
  const { notify } = React.useContext(SnackbarContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tenantId, setTenantId] = React.useState<string | null>(null);
  const [connectors, setConnectors] = React.useState<ConnectorFull[]>([]);
  const [loadingConnectors, setLoadingConnectors] = React.useState(true);
  const [domainContext, setDomainContext] = React.useState('');
  const [starting, setStarting] = React.useState(false);
  const [startError, setStartError] = React.useState<string | null>(null);

  // Domain context modal
  const [domainModalOpen, setDomainModalOpen] = React.useState(false);
  const [domainDraft, setDomainDraft] = React.useState('');
  const [savingDomain, setSavingDomain] = React.useState(false);
  const [rebuildFromScratch, setRebuildFromScratch] = React.useState(false);

  // Generic table/resource picker modal (used for SQL + REST connectors)
  const [pickerConnector, setPickerConnector] = React.useState<ConnectorFull | null>(null);
  // Klaris-owned Drive files modal (handles add/remove + per-file sheet selection)
  const [driveModalConnector, setDriveModalConnector] = React.useState<ConnectorFull | null>(null);
  const buildModelBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const [editBtnAnchor, setEditBtnAnchor] = React.useState<HTMLButtonElement | null>(null);

  // History
  const [history, setHistory] = React.useState<SyncJob[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);

  const authHeader = React.useCallback((): Record<string, string> => {
    const token = localStorage.getItem('klaris_jwt');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  React.useEffect(() => {
    const raw = localStorage.getItem('klaris_tenant');
    if (!raw) return;
    try { const t = JSON.parse(raw); setTenantId(t.tenant_id || t.id || null); }
    catch { setTenantId(raw); }
  }, []);

  const loadConnectors = React.useCallback(() => {
    if (!tenantId) return;
    setLoadingConnectors(true);
    fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors`), { headers: authHeader() as HeadersInit })
      .then(r => r.json())
      .then((data: any) => {
        const active: ConnectorFull[] = (Array.isArray(data) ? data : data.connectors || []).filter(
          (c: ConnectorFull) => c.status === 'active',
        );
        setConnectors(active);
      })
      .catch(e => console.error('Failed to load connectors', e))
      .finally(() => setLoadingConnectors(false));
  }, [tenantId, authHeader]);

  // Open Klaris-owned Drive picker. The modal itself launches Google's native
  // picker as a sub-flow when the user wants to add new files, but the
  // canonical selection state (including remove + per-file sheet picks) lives
  // in our UI — fixing the long-standing UX gap where the native picker was
  // write-only and removal required visiting the connector detail page.
  const handleDriveEdit = React.useCallback((conn: ConnectorFull) => {
    setDriveModalConnector(conn);
  }, []);

  const loadHistory = React.useCallback(() => {
    if (!tenantId) return;
    setHistoryLoading(true);
    fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/sync-jobs?limit=5`), { headers: authHeader() as HeadersInit })
      .then(r => r.json())
      .then((data: SyncJob[]) => setHistory(Array.isArray(data) ? data : []))
      .catch(e => console.error('Failed to load sync history', e))
      .finally(() => setHistoryLoading(false));
  }, [tenantId, authHeader]);

  React.useEffect(() => { loadConnectors(); }, [loadConnectors]);
  React.useEffect(() => { loadHistory(); }, [loadHistory]);

  // Load saved domain context from tenant properties
  React.useEffect(() => {
    if (!tenantId) return;
    fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/properties`), { headers: authHeader() as HeadersInit })
      .then(r => r.ok ? r.json() : { properties: {} })
      .then(data => {
        const saved = data?.properties?.domain_context || '';
        setDomainContext(saved);
      })
      .catch(() => {});
  }, [tenantId, authHeader]);

  // A connector is "ready" when it has ≥1 selected item
  const allReady = connectors.length > 0 && connectors.every(c => selectionCount(c) > 0);
  const notReadyCount = connectors.filter(c => selectionCount(c) === 0).length;
  const firstUnreadyConnectorId = connectors.find((c: ConnectorFull) => selectionCount(c) === 0)?.connector_id;
  const showOwnDataNudges = shouldShow && currentStepId === 'data_model_built';

  // After connectors render, imperatively find the edit-button anchor for the nudge
  React.useEffect(() => {
    if (!showOwnDataNudges || allReady) { setEditBtnAnchor(null); return; }
    const el = document.querySelector<HTMLButtonElement>('[data-nudge="select-tables"]');
    setEditBtnAnchor(el);
  }, [connectors, showOwnDataNudges, allReady]);

  const handleStartSync = async (contextOverride?: string) => {
    if (!tenantId || !allReady) return;
    setStarting(true);
    setStartError(null);
    const effectiveContext = contextOverride !== undefined ? contextOverride : domainContext;
    try {
      const r = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/sync-jobs`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          connector_ids: connectors.map((c: ConnectorFull) => c.connector_id),
          domain_context: effectiveContext.trim() || null,
          rebuild_from_scratch: rebuildFromScratch,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error((err as any).detail || `Server error ${r.status}`);
      }
      const job: SyncJob = await r.json();
      navigate(`/data-sync/${job.job_id}`);
    } catch (e: any) {
      setStartError(e.message || 'Failed to start sync');
    } finally {
      setStarting(false);
    }
  };

  const runningJob = history.find((j: SyncJob) => j.status === 'running' || j.status === 'pending');

  if (loadingConnectors) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0, pb: { xs: 2, md: 0 } }}>
      {/* Page header */}
      <Box sx={(t) => ({ mb: 2.5, pb: 1.5, borderBottom: `1px solid ${t.tokens.border.subtle}` })}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 2, md: 0 }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.5, flex: { md: 1 }, minWidth: 0 }}>
            Refreshes schema, profiles tables, then uses <strong>AI</strong> to update your data model incrementally
          </Typography>
          <Tooltip
            title={
              runningJob ? 'A sync is already running - wait for it to finish' :
              notReadyCount > 0 ? `${notReadyCount} connector${notReadyCount !== 1 ? 's' : ''} still need${notReadyCount === 1 ? 's' : ''} table selection` : ''
            }
            placement="left"
          >
            <span style={{ display: 'block', width: isMobile ? '100%' : 'auto' }}>
              <Button
                ref={buildModelBtnRef}
                variant="contained"
                size="medium"
                fullWidth={isMobile}
                startIcon={starting ? <CircularProgress size={15} color="inherit" /> : <AccountTreeRounded />}
                onClick={() => { if (!allReady || starting || !!runningJob) return; setDomainDraft(domainContext); setRebuildFromScratch(false); setDomainModalOpen(true); }}
                disabled={!allReady || starting || !!runningJob}
                sx={{ fontWeight: 700, px: 2.5, py: 0.875, fontSize: '0.875rem', borderRadius: 2 }}
              >
                {starting ? 'Starting…' : 'Build Data Model'}
              </Button>
            </span>
          </Tooltip>
          <OnboardingCoachmark
            open={Boolean(showOwnDataNudges && allReady && !starting && !runningJob)}
            anchorRef={buildModelBtnRef}
            message="Now click Build Data Model."
            placement="bottom-end"
          />
        </Stack>
      </Box>

      {/* Pipeline explainer (includes scope note) */}
      <PipelineExplainer connectorCount={connectors.length} />

      {startError && !runningJob && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{startError}</Alert>}

      {/* Two-column layout */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2.5, md: 3 }, alignItems: 'stretch' }}>

        {/* Left: connectors + domain context */}
        <Box sx={{ flex: '1 1 0', minWidth: 0, order: { xs: 1, md: 1 } }}>

          {/* Connector readiness table */}
          <Paper variant="outlined" sx={(t) => ({ borderRadius: `${t.ds.radius.md}px`, overflow: 'hidden', borderColor: t.tokens.border.subtle, bgcolor: t.tokens.surface.raised })}>
            <Box sx={(t) => ({
              px: { xs: 1.5, sm: 2 }, py: 1,
              borderBottom: `1px solid ${t.tokens.border.default}`,
              bgcolor: t.tokens.surface.sunken,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 0.75, sm: 0 },
            })}>
              <Typography variant="caption" fontWeight={600} color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem' }}>
                Active Connectors ({connectors.length})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {connectors.filter((c: ConnectorFull) => selectionCount(c) > 0).length} / {connectors.length} ready
              </Typography>
            </Box>

            {connectors.length === 0 ? (
              <Box sx={{ px: 2.5, py: 5, textAlign: 'center' }}>
                <StorageRounded sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">No active connectors.</Typography>
                <Link
                  component={RouterLink}
                  to="/connectors"
                  underline="hover"
                  sx={{
                    mt: 1,
                    display: 'inline-block',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                  }}
                >
                  Add connector →
                </Link>
              </Box>
            ) : connectors.map((conn: ConnectorFull, idx: number) => {
              const count = selectionCount(conn);
              const label = selectionLabel(conn);
              const ready = count > 0;
              const isDrive = conn.type === 'google_drive';
              return (
                <Box
                  key={conn.connector_id}
                  sx={(t) => ({
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: { xs: 1.25, sm: 2 },
                    px: { xs: 1.5, sm: 2 }, py: 1.5,
                    borderBottom: idx < connectors.length - 1 ? `1px solid ${t.tokens.border.subtle}` : 'none',
                  })}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: { sm: 1 } }}>
                    <ConnectorIcon type={conn.type} size={20} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={500} noWrap title={conn.name || conn.type}>
                        {conn.name || conn.type}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                        {conn.type.replace(/_/g, ' ')}
                      </Typography>
                    </Box>
                  </Box>

                  {/*
                   * Selection affordance.
                   *
                   * Replaces the old chip+pencil duo with a single labeled
                   * button per row. Two reasons:
                   *   1. The pencil-icon button next to a status chip was
                   *      ambiguous — users couldn't tell whether clicking
                   *      it would edit the connector, the status, or the
                   *      selection.
                   *   2. Collapsing both into one element removes the
                   *      "two affordances, which one do I click" cognitive
                   *      load and gives empty rows a clear primary CTA.
                   *
                   * Ready  → outlined success-tinted button "✓ 5 tables ✎"
                   *          (informational + obviously editable)
                   * Empty  → contained primary button "Select tables"
                   *          (prominent verb-driven CTA)
                   */}
                  <Box
                    sx={{
                      flexShrink: 0,
                      alignSelf: { xs: 'stretch', sm: 'center' },
                      display: 'flex',
                      justifyContent: { xs: 'flex-end', sm: 'flex-end' },
                    }}
                  >
                    {ready ? (
                      <Tooltip title={`Edit ${label} selection`}>
                        <Button
                          data-nudge={showOwnDataNudges && !allReady && conn.connector_id === firstUnreadyConnectorId ? 'select-tables' : undefined}
                          variant="outlined"
                          size="small"
                          color="success"
                          disableElevation
                          startIcon={<CheckCircleRounded sx={{ fontSize: '15px !important' }} />}
                          endIcon={<EditRounded sx={{ fontSize: '14px !important', opacity: 0.7 }} />}
                          onClick={() => isDrive ? handleDriveEdit(conn) : setPickerConnector(conn)}
                          sx={(t) => ({
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.8125rem',
                            borderRadius: `${t.ds.radius.sm}px`,
                            px: 1.25,
                            py: 0.375,
                            minHeight: 28,
                            // Soften the success colour so it reads as a
                            // status pill rather than a primary CTA.
                            color: 'success.dark',
                            borderColor: t.tokens.border.subtle,
                            bgcolor: 'transparent',
                            '&:hover': {
                              borderColor: 'success.main',
                              bgcolor: t.tokens.surface.sunken,
                            },
                          })}
                        >
                          {count} {label}
                        </Button>
                      </Tooltip>
                    ) : (
                      <Tooltip title={`Select ${label} to sync from this connector`}>
                        <Button
                          data-nudge={showOwnDataNudges && !allReady && conn.connector_id === firstUnreadyConnectorId ? 'select-tables' : undefined}
                          variant="contained"
                          size="small"
                          color="primary"
                          disableElevation
                          startIcon={<WarningAmberRounded sx={{ fontSize: '15px !important' }} />}
                          onClick={() => isDrive ? handleDriveEdit(conn) : setPickerConnector(conn)}
                          sx={(t) => ({
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.8125rem',
                            borderRadius: `${t.ds.radius.sm}px`,
                            px: 1.5,
                            py: 0.375,
                            minHeight: 28,
                          })}
                        >
                          Select {label}
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Paper>

        </Box>

        {/* Right: past runs */}
        <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, order: { xs: 2, md: 2 } }}>
          <Paper variant="outlined" sx={(t) => ({ borderRadius: `${t.ds.radius.md}px`, overflow: 'hidden', borderColor: t.tokens.border.subtle, bgcolor: t.tokens.surface.raised })}>
            <Box sx={(t) => ({
              px: { xs: 1.5, sm: 2 }, py: 1, borderBottom: `1px solid ${t.tokens.border.default}`,
              bgcolor: t.tokens.surface.sunken,
              display: 'flex', alignItems: 'center', gap: 1,
            })}>
              <HistoryRounded sx={{ fontSize: 15, color: 'text.secondary' }} />
              <Typography variant="caption" fontWeight={600} color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem' }}>
                Past Runs
              </Typography>
            </Box>
            {historyLoading ? (
              <Box sx={{ px: 2, py: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress size={20} /></Box>
            ) : history.length === 0 ? (
              <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">No past runs yet</Typography>
              </Box>
            ) : history.map((j: SyncJob, idx: number) => {
              const durMs = j.completed_at
                ? parseUtcDate(j.completed_at).getTime() - parseUtcDate(j.created_at).getTime()
                : null;
              return (
                <Box key={j.job_id} onClick={() => navigate(`/data-sync/${j.job_id}`)}
                  sx={(t) => ({
                    px: { xs: 1.5, sm: 2 }, py: 1.5, cursor: 'pointer',
                    borderBottom: idx < history.length - 1 ? `1px solid ${t.tokens.border.subtle}` : 'none',
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    minWidth: 0,
                    '&:hover': { bgcolor: t.tokens.surface.sunken },
                  })}
                >
                  <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                      <StatusChip status={j.status} />
                      <Typography variant="caption" color="text.secondary">
                        {j.connector_ids.length} connector{j.connector_ids.length !== 1 ? 's' : ''}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {formatRelativeTime(j.created_at)}
                      {durMs !== null && ` · ${formatDuration(durMs)}`}
                    </Typography>
                  </Box>
                  <ArrowForwardIosRounded sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }} />
                </Box>
              );
            })}
          </Paper>
        </Box>
      </Box>
      <OnboardingCoachmark
        open={Boolean(showOwnDataNudges && !allReady && editBtnAnchor && !pickerConnector && !driveModalConnector)}
        anchorEl={editBtnAnchor}
        message="Select tables first."
        placement="top-end"
        maxWidth={300}
      />

      {/* Inline picker modal (SQL / REST connectors) */}
      <TablePickerModal
        open={!!pickerConnector}
        connector={pickerConnector}
        tenantId={tenantId || ''}
        authHeader={authHeader}
        onClose={() => setPickerConnector(null)}
        onSaved={loadConnectors}
      />

      {/* Klaris-owned Drive files modal (add/remove + per-file sheet picker) */}
      {driveModalConnector && (
        <DriveFilesPickerModal
          open={!!driveModalConnector}
          connectorId={driveModalConnector.connector_id}
          connectorName={driveModalConnector.name}
          tenantId={tenantId || ''}
          authHeader={authHeader}
          initialFiles={(driveModalConnector.connector_metadata?.selected_drive_files || []) as DriveFile[]}
          onClose={() => setDriveModalConnector(null)}
          onSaved={loadConnectors}
        />
      )}

      <AppDialog
        open={domainModalOpen}
        onClose={() => setDomainModalOpen(false)}
        size="sm"
        icon={<AccountTreeRounded sx={{ color: 'primary.main' }} />}
        title="Build data model"
        subtitle={`Runs on all ${connectors.length} active connector${connectors.length !== 1 ? 's' : ''} — refresh schema, profile tables, then AI applies incremental updates while preserving your edits.`}
        busy={starting}
        primaryAction={{
          label: starting ? 'Starting…' : 'Start build',
          icon: <AccountTreeRounded />,
          onClick: async () => {
            if (!tenantId) return;
            setSavingDomain(true);
            try {
              await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/properties`), {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ properties: { domain_context: domainDraft.trim() } }),
              });
              setDomainContext(domainDraft.trim());
            } catch { /* non-blocking */ } finally {
              setSavingDomain(false);
            }
            setDomainModalOpen(false);
            handleStartSync(domainDraft.trim());
          },
        }}
        secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: () => setDomainModalOpen(false) }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
          <AutoAwesomeRounded sx={{ fontSize: 14, color: 'primary.main' }} />
          <Typography variant="caption" fontWeight={600} color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem' }}>
            AI Context <Typography component="span" variant="caption" color="text.disabled">(optional)</Typography>
          </Typography>
        </Stack>
        <TextField
          fullWidth multiline minRows={4} maxRows={10} autoFocus
          placeholder={'Describe your business to help AI build better models - e.g. "E-commerce platform selling apparel. Orders link to customers and products…"'}
          value={domainDraft}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDomainDraft(e.target.value)}
          size="small"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8125rem' } }}
        />
        <Box
          sx={{ display: 'flex', alignItems: 'center', mt: 2, cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setRebuildFromScratch(v => !v)}
        >
          <Checkbox
            checked={rebuildFromScratch}
            onChange={e => setRebuildFromScratch(e.target.checked)}
            size="small"
            color="error"
            sx={{ p: 0, mr: 1 }}
            onClick={e => e.stopPropagation()}
          />
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
            Rebuild from scratch
          </Typography>
        </Box>
        {rebuildFromScratch && (
          <Alert severity="warning" icon={<WarningAmberRounded fontSize="small" />} sx={{ mt: 1.5, borderRadius: 2, fontSize: '0.8125rem' }}>
            The AI will re-analyse all your data sources and build a brand-new data model. Your existing entities, fields, relationships, and manual edits will be overwritten by the new model. This cannot be undone.
          </Alert>
        )}
      </AppDialog>
    </Box>
  );
}
